/**
 * Key actives inside the PDP 'Ingredients' row (snippets/pdp-cb-ingredients.liquid).
 *
 * - Animates each active's <details>, and the full ingredient list, open and
 *   shut. Native <details> snaps, and the rows around them animate, so these
 *   match: same 0.3s curve as main.css's .disclosure__panel.
 * - Keeps one active open at a time. Done here rather than with the <details>
 *   `name` attribute, because the browser would close the other one instantly
 *   instead of letting it animate shut.
 * - 'View more' reveals the actives past the block's limit, growing the list
 *   smoothly.
 *
 * Without JS every active shows and the <details> toggle natively; the CSS only
 * hides extras and shows the button once this element is :defined.
 *
 * The height animation runs on .pdp-actives__panel, a wrapper with no padding,
 * so the measured height is exactly the content's. The outer row's
 * details-disclosure ignores these transitions: it only reacts to events whose
 * target is its own panel, and Web Animations fire no transition events at all.
 */
if (!customElements.get('pdp-actives')) {
  const DURATION = 300;
  const EASING = 'cubic-bezier(0.2, 0.6, 0.4, 1)';

  class PdpActives extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    handleClick(evt) {
      const more = evt.target.closest('.pdp-actives__more');
      if (more && this.contains(more)) {
        this.showAll(more);
        return;
      }

      const summary = evt.target.closest('summary');
      if (!summary || !this.contains(summary)) return;

      const details = summary.parentElement;
      if (!details.classList.contains('pdp-actives__details')) return;

      evt.preventDefault();

      // 'open' stays true for the whole close animation, so a row that is
      // mid-close counts as closed and a click reopens it.
      const isOpen = details.open && !details.classList.contains('is-closing');

      if (isOpen) {
        this.toggle(details, false);
      } else {
        if (details.hasAttribute('data-exclusive')) {
          this.querySelectorAll('details[data-exclusive][open]').forEach((other) => {
            if (other !== details && !other.classList.contains('is-closing')) {
              this.toggle(other, false);
            }
          });
        }
        this.toggle(details, true);
      }
    }

    /**
     * Opens or closes one <details>, animating its panel's height from wherever
     * it is now, so a click mid-animation reverses smoothly instead of jumping.
     * @param {HTMLDetailsElement} details
     * @param {boolean} opening
     */
    toggle(details, opening) {
      const panel = details.querySelector(':scope > .pdp-actives__panel');

      // Read the in-flight height before cancelling, while the animation still
      // drives layout. A closed row has no rendered panel, so it starts at 0.
      const from = details.open && panel ? panel.offsetHeight : 0;
      if (details.pdpAnimation) details.pdpAnimation.cancel();
      if (details.pdpFade) details.pdpFade.cancel();

      details.classList.toggle('is-closing', !opening);
      if (opening) details.open = true;

      if (!panel || this.reducedMotion.matches) {
        this.finish(details, opening);
        return;
      }

      const to = opening ? panel.scrollHeight : 0;

      // A close holds its 0px end state until 'open' is removed, or the panel
      // would flash back to full height for a frame before the finish handler
      // runs. An open ends on the panel's natural height, so it holds nothing.
      const animation = this.animateHeight(panel, from, to, !opening);
      details.pdpAnimation = animation;

      // The content fades as well, as main.css does for the outer rows'
      // .disclosure__content: in over 0.3s after a 0.1s delay, out over 0.3s.
      const content = panel.firstElementChild;
      if (content) {
        details.pdpFade = content.animate(
          opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
          { duration: DURATION, delay: opening ? 100 : 0, easing: 'ease', fill: opening ? 'backwards' : 'forwards' }
        );
      }

      animation.onfinish = () => {
        details.pdpAnimation = null;
        this.finish(details, opening);

        // A close held its end state; release it now that the row is shut.
        // An open's fade is left alone: it runs 0.1s past the height, and the
        // next toggle cancels it through details.pdpFade if it is still going.
        if (!opening) {
          animation.cancel();
          panel.style.overflow = '';
          if (details.pdpFade) details.pdpFade.cancel();
          details.pdpFade = null;
        }
      };
    }

    finish(details, opening) {
      details.classList.remove('is-closing');
      if (!opening) details.open = false;
    }

    /**
     * Reveals the actives hidden past the block's limit.
     * @param {HTMLButtonElement} button
     */
    showAll(button) {
      const list = this.querySelector('.pdp-actives__list');
      const from = list ? list.offsetHeight : 0;

      this.classList.add('is-expanded');
      button.setAttribute('aria-expanded', 'true');

      if (list && !this.reducedMotion.matches) {
        this.animateHeight(list, from, list.offsetHeight);
      }

      // The button disappears, so move focus to the first newly shown active
      // rather than letting it fall back to the page body.
      const firstExtra = this.querySelector('.pdp-actives__item--extra summary');
      if (firstExtra) firstExtra.focus({ preventScroll: true });
    }

    animateHeight(element, from, to, holdEnd = false) {
      element.style.overflow = 'hidden';
      const animation = element.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        duration: DURATION,
        easing: EASING,
        fill: holdEnd ? 'forwards' : 'none',
      });

      // A cancelled animation reports asynchronously, after its replacement has
      // already started, so only restore overflow once nothing is running.
      const cleanUp = () => {
        if (element.getAnimations().length === 0) element.style.overflow = '';
      };
      animation.addEventListener('finish', cleanUp);
      animation.addEventListener('cancel', cleanUp);

      return animation;
    }
  }

  customElements.define('pdp-actives', PdpActives);
}
